<!--
  1237 수시 근골격계 부담작업 유해요인조사

  구성
  - 작성자 정보 / 사업장 정보 (FieldSelectModule: 사업장명 선택 시 부서명·팀명·담당주무관 자동입력)
  - 조사구분 (정기조사 / 수시조사 3종, 복수 선택)
  - 조사정보 (조사일시·조사자·작업공정명·작업명)
  - 가. 작업장 상황 조사 (라디오 + "언제부터" 입력)
  - 나. 작업조건 조사
      1단계 작업별 과제 내용 / 2단계 작업부하·빈도(총점수 = A×B 자동) / 3단계 유해요인 평가 + 작업별 원인분석
  - 작업환경 개선계획서 (총점수는 2단계 표에서 같은 단위작업명의 총점수를 자동 입력)
  - 작업환경 개선계획 및 결과보고서
  - 파일첨부
-->
<template>
  <div class="write-1237">
    <!-- 작성자 정보 / 사업장 정보 -->
    <field-select-module
      ref="fieldSelectModule"
      v-model="form.stdInfo"
      :board="board"
      :is-print="isPrint"
      :field-required="true"
      title="사업장 정보"
      :types="['field_name', '부서명_과', 'com_name', 'manager_name']"
      :typeslabel="{ field_name: '사업장명', 부서명_과: '부서명', com_name: '팀명', manager_name: '담당주무관' }"
    />

    <!-- 조사구분 -->
    <div class="section">
      <h5 class="section-title"><i class="dot"></i>조사구분</h5>
      <div class="survey-type-row">
        <span class="survey-type-label">정기조사 :</span>
        <check-item v-model="form.조사구분.정기조사" label="정기조사" :is-print="isPrint" :readonly="board.readonly" />
      </div>
      <div class="survey-type-row">
        <span class="survey-type-label">수시조사 :</span>
        <check-item v-model="form.조사구분.근골격계질환자발생시" label="근골격계질환자 발생시" :is-print="isPrint" :readonly="board.readonly" />
        <check-item v-model="form.조사구분.새로운작업설비도입시" label="새로운 작업 · 설비도입시" :is-print="isPrint" :readonly="board.readonly" />
        <check-item v-model="form.조사구분.작업환경변경" label="업무의 양과 작업공정 등 작업환경 변경" :is-print="isPrint" :readonly="board.readonly" />
      </div>
    </div>

    <!-- 조사정보 -->
    <div class="section">
      <h5 class="section-title"><i class="dot"></i>조사정보</h5>
      <div class="grid grid-cols-12 gap-x-6 gap-y-4">
        <div class="col-span-12 xl:col-span-4 flex items-center">
          <div class="label-text">조사일시</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.조사정보.조사일시 }}</p>
          <date-selector v-else v-model="form.조사정보.조사일시" :readonly="board.readonly" placeholder="날짜선택" class="w-full flex-1" />
        </div>
        <div class="col-span-12 xl:col-span-4 flex items-center">
          <div class="label-text">조사자</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.조사정보.조사자 }}</p>
          <ml-input v-else v-model="form.조사정보.조사자" :readonly="board.readonly" placeholder="담당자1, 담당자2" class="w-full flex-1" />
        </div>
        <div class="col-span-12 xl:col-span-8 flex items-center">
          <div class="label-text">작업공정명</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.조사정보.작업공정명 }}</p>
          <ml-input v-else v-model="form.조사정보.작업공정명" :readonly="board.readonly" placeholder="읍면지역 가로청소" class="w-full flex-1" />
        </div>
        <div class="col-span-12 xl:col-span-8 flex items-center">
          <div class="label-text">작업명</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.조사정보.작업명 }}</p>
          <ml-input v-else v-model="form.조사정보.작업명" :readonly="board.readonly" placeholder="읍면지역 가로청소" class="w-full flex-1" />
        </div>
      </div>
    </div>

    <!-- 가. 작업장 상황 조사 -->
    <div class="section">
      <h5 class="section-title"><i class="dot"></i>가. 작업장 상황 조사</h5>
      <situation-radio-row v-model="form.상황조사.작업설비" title="1. 작업 설비" radio-name="situation_1" :items="situationItemsEquipment" :is-print="isPrint" :readonly="board.readonly" />
      <situation-radio-row v-model="form.상황조사.작업량" title="2. 작업량" radio-name="situation_2" :items="situationItems" :is-print="isPrint" :readonly="board.readonly" />
      <situation-radio-row v-model="form.상황조사.작업속도" title="3. 작업 속도" radio-name="situation_3" :items="situationItems" :is-print="isPrint" :readonly="board.readonly" />
      <situation-radio-row v-model="form.상황조사.업무변화" title="4. 업무 변화" radio-name="situation_4" :items="situationItems" :is-print="isPrint" :readonly="board.readonly" />
    </div>

    <!-- 나. 작업조건 조사 -->
    <div class="section">
      <h5 class="section-title"><i class="dot"></i>나. 작업조건 조사 (인간공학적인 측면을 고려한 조사)</h5>

      <!-- 1단계 -->
      <p class="step-title">1단계 : 작업별 과제 내용 조사 (유해요인 조사자)</p>
      <div class="table-scroll">
        <table class="doc-table">
          <colgroup>
            <col width="60" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th colspan="2" class="text-left">
                작업명 : <span class="font-normal">{{ form.조사정보.작업명 || "-" }}</span>
              </th>
            </tr>
            <tr>
              <th colspan="2" class="text-left">
                <div class="flex items-center justify-between">
                  <span>작업내용</span>
                  <row-controls v-if="editable" add-label="작업내용추가" @add="addTaskContent" @remove-last="removeLast(form.작업내용목록)" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in form.작업내용목록" :key="`task_${item.id}`">
              <td class="text-center">{{ index + 1 }}</td>
              <td>
                <p v-if="isPrint" class="print-input">{{ item.내용 }}</p>
                <ml-input v-else v-model="item.내용" :readonly="board.readonly" placeholder="입력필드" class="w-full" />
              </td>
            </tr>
            <tr v-if="form.작업내용목록.length === 0">
              <td colspan="2" class="text-center empty">{{ isPrint ? "-" : "작업내용을 추가해주세요" }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2단계 -->
      <p class="step-title mt-8">2단계 : 각 작업별 작업부하 및 작업빈도 (근로자 면담)</p>
      <div class="table-scroll">
        <table class="doc-table score-guide">
          <thead>
            <tr>
              <th>작업부하(A)</th>
              <th width="120">점수</th>
              <th>작업빈도(B)</th>
              <th width="120">점수</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in scoreGuide" :key="row.load">
              <td class="text-center">{{ row.load }}</td>
              <td class="text-center">{{ row.score }}</td>
              <td class="text-center">{{ row.frequency }}</td>
              <td class="text-center">{{ row.score }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="editable" class="flex justify-end mt-4">
        <row-controls add-label="단위작업추가" @add="addUnitTask" @remove-last="removeLastUnitTask" />
      </div>
      <div class="table-scroll mt-2">
        <table class="doc-table">
          <colgroup>
            <col width="60" />
            <col />
            <col width="18%" />
            <col width="18%" />
            <col width="18%" />
            <col width="18%" />
          </colgroup>
          <thead>
            <tr>
              <th>No.</th>
              <th>단위작업명</th>
              <th>
                <span class="inline-flex items-center justify-center gap-1">
                  부담작업(호)
                  <feather-icon v-if="!isPrint" icon="AlertCircleIcon" class="info-icon" @click="showBurdenPopup = true" />
                </span>
              </th>
              <th>작업부하(A)</th>
              <th>작업빈도(B)</th>
              <th>총점수(A×B)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in form.단위작업목록" :key="`unit_${item.id}`">
              <td class="text-center">{{ index + 1 }}</td>
              <td>
                <p v-if="isPrint" class="print-input">{{ item.단위작업명 }}</p>
                <ml-input v-else v-model="item.단위작업명" :readonly="board.readonly" placeholder="가로 청소 작업" class="w-full" />
              </td>
              <td>
                <p v-if="isPrint" class="print-input">{{ item.부담작업 }}</p>
                <ml-input v-else v-model="item.부담작업" :readonly="board.readonly" placeholder="2, 4호" class="w-full" />
              </td>
              <td>
                <p v-if="isPrint" class="print-input text-center">{{ item.작업부하 }}</p>
                <ml-input v-else-if="board.readonly" :value="item.작업부하" readonly class="w-full" />
                <vs-select v-else v-model="item.작업부하" placeholder="1~5" class="w-full" autocomplete>
                  <vs-select-item v-for="score in scoreOptions" :key="`load_${item.id}_${score}`" :text="score" :value="score" />
                </vs-select>
              </td>
              <td>
                <p v-if="isPrint" class="print-input text-center">{{ item.작업빈도 }}</p>
                <ml-input v-else-if="board.readonly" :value="item.작업빈도" readonly class="w-full" />
                <vs-select v-else v-model="item.작업빈도" placeholder="1~5" class="w-full" autocomplete>
                  <vs-select-item v-for="score in scoreOptions" :key="`freq_${item.id}_${score}`" :text="score" :value="score" />
                </vs-select>
              </td>
              <td>
                <p v-if="isPrint" class="print-input text-center">{{ item.총점수 }}</p>
                <ml-input v-else :value="item.총점수" readonly placeholder="자동입력" class="w-full" />
              </td>
            </tr>
            <tr v-if="form.단위작업목록.length === 0">
              <td colspan="6" class="text-center empty">{{ isPrint ? "-" : "단위작업을 추가해주세요" }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 3단계 -->
      <p class="step-title mt-8">3단계 : 유해요인 및 원인 평가서</p>
      <div class="table-scroll">
        <table class="doc-table">
          <colgroup>
            <col width="14%" />
            <col width="36%" />
            <col width="14%" />
            <col width="36%" />
          </colgroup>
          <tbody>
            <tr>
              <th>직종명</th>
              <td>
                <p v-if="isPrint" class="print-input">{{ form.유해요인평가.직종명 }}</p>
                <ml-input v-else v-model="form.유해요인평가.직종명" :readonly="board.readonly" placeholder="읍면지역 가로 청소" class="w-full" />
              </td>
              <th>근로자(명)</th>
              <td>
                <p v-if="isPrint" class="print-input">{{ form.유해요인평가.근로자수 }}</p>
                <ml-input v-else v-model="form.유해요인평가.근로자수" :readonly="board.readonly" placeholder="00명" class="w-full" />
              </td>
            </tr>
            <tr>
              <td colspan="4">
                <p v-if="isPrint" class="print-input text-center">{{ form.유해요인평가.유해요인설명 }}</p>
                <ml-input v-else v-model="form.유해요인평가.유해요인설명" :readonly="board.readonly" placeholder="<유해요인 설명> 가로 청소(반복작업, 구부림 - 2호, 4호)" class="w-full centered-input" />
              </td>
            </tr>
            <tr>
              <td v-for="photo in form.평가사진" :key="`photo_${photo.id}`" colspan="2" class="photo-cell">
                <div v-if="isPrint" class="print-photo">
                  <img v-if="photo.image && photo.image.path" :src="photo.image.path" />
                </div>
                <div v-else class="photo-box">
                  <file-uploader-transparent v-model="photo.image" :readonly="board.readonly" style="background-size: contain; background-repeat: no-repeat" @progress="(state) => onPhotoProgress(photo, state)" />
                  <simple-spinner v-if="photo.uploading" class="photo-spinner" />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 작업별로 관찰된 유해요인 원인분석 -->
      <div class="flex items-center justify-between mt-8 mb-3">
        <p class="step-title mb-0">작업별로 관찰된 유해요인 원인분석</p>
        <row-controls v-if="editable" add-label="단위작업추가" @add="addCauseUnit" @remove-last="removeLast(form.원인분석목록)" />
      </div>
      <!-- cause-table-wrap: 표 안의 입력에 포커스가 있으면(:focus-within) 그 단위작업 표를 강조해 어느 단위작업의 유해요인인지 알 수 있게 한다 -->
      <div v-for="(unit, unitIndex) in form.원인분석목록" :key="`cause_${unit.id}`" class="table-scroll cause-table-wrap" :class="unitIndex > 0 && 'mt-5'">
        <!-- 5열 구성: 유해요인 열(1~2열, 28%)이 단위작업명 라벨(1열, 14%)의 두 배가 되도록 한다 -->
        <table class="doc-table cause-table">
          <colgroup>
            <col width="14%" />
            <col width="14%" />
            <col width="22%" />
            <col width="14%" />
            <col width="36%" />
          </colgroup>
          <tbody>
            <tr>
              <th>단위작업명</th>
              <td colspan="2">
                <p v-if="isPrint" class="print-input">{{ unit.단위작업명 }}</p>
                <ml-input v-else v-model="unit.단위작업명" :readonly="board.readonly" placeholder="가로 청소 작업" class="w-full" />
              </td>
              <th>
                <span class="inline-flex items-center justify-center gap-1">
                  부담작업(호)
                  <feather-icon v-if="!isPrint" icon="AlertCircleIcon" class="info-icon" @click="showBurdenPopup = true" />
                </span>
              </th>
              <td>
                <p v-if="isPrint" class="print-input">{{ unit.부담작업 }}</p>
                <ml-input v-else v-model="unit.부담작업" :readonly="board.readonly" placeholder="2호, 4호" class="w-full" />
              </td>
            </tr>
            <tr v-if="editable">
              <td colspan="5" class="cause-controls">
                <row-controls add-label="유해요인추가" @add="addCauseFactor(unit)" @remove-last="removeLast(unit.유해요인목록)" />
              </td>
            </tr>
            <tr>
              <th colspan="2">유해요인</th>
              <th colspan="3">유해요인에 대한 원인</th>
            </tr>
            <tr v-for="factor in unit.유해요인목록" :key="`factor_${unit.id}_${factor.id}`">
              <td colspan="2">
                <p v-if="isPrint" class="print-input">{{ factor.유해요인 }}</p>
                <ml-input v-else v-model="factor.유해요인" :readonly="board.readonly" placeholder="반복동작(2호)" class="w-full" />
              </td>
              <td colspan="3">
                <p v-if="isPrint" class="print-textarea no-block">{{ factor.원인 }}</p>
                <ml-textarea v-else v-model="factor.원인" :readonly="board.readonly" rows="3" placeholder="유해요인에 대한 원인" class="w-full mb-0" />
              </td>
            </tr>
            <tr v-if="unit.유해요인목록.length === 0">
              <td colspan="5" class="text-center empty">{{ isPrint ? "-" : "유해요인을 추가해주세요" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="form.원인분석목록.length === 0" class="empty-message">{{ isPrint ? "-" : "단위작업을 추가하면 표가 생성됩니다" }}</p>
    </div>

    <!-- 작업환경 개선계획서 -->
    <div class="section">
      <div class="flex items-center justify-between mb-4">
        <h5 class="section-title mb-0"><i class="dot"></i>작업환경 개선계획서</h5>
        <div v-if="editable">
          <vs-button color="secondary" @click="addPlan">항목추가</vs-button>
          <vs-button class="ml-4" color="secondary" type="border" :disabled="planCheckIds.length === 0" @click="removePlans">선택삭제</vs-button>
        </div>
      </div>
      <div class="table-scroll">
        <table class="doc-table plan-table">
          <colgroup>
            <col v-if="editable" width="36" />
            <col width="11%" />
            <col width="11%" />
            <col width="18%" />
            <col width="18%" />
            <col width="18%" />
            <col width="110" />
            <col width="130" />
          </colgroup>
          <thead>
            <tr>
              <th v-if="editable" class="p-0"><vs-checkbox :value="form.개선계획목록.length > 0 && planCheckIds.length === form.개선계획목록.length" class="check" @click.native="toggleAllPlans" /></th>
              <th>작업명</th>
              <th>단위 작업명</th>
              <th>문제점 (유해요인 원인)</th>
              <th>근로자의 의견</th>
              <th>개선방안</th>
              <th>추진 일정<br />/ 개선비용(만원)</th>
              <th>개선우선순위</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in form.개선계획목록" :key="`plan_${item.id}`">
              <td v-if="editable" class="p-0 check-cell"><vs-checkbox v-model="planCheckIds" :vs-value="item.id" class="check" /></td>
              <td class="text-center auto-cell">
                {{ form.조사정보.작업명 || "-" }}
              </td>
              <td>
                <p v-if="isPrint" class="print-input">{{ item.단위작업명 }}</p>
                <ml-input v-else v-model="item.단위작업명" :readonly="board.readonly" placeholder="가로 청소 작업" class="w-full" />
              </td>
              <td>
                <p v-if="isPrint" class="print-textarea no-block">{{ item.문제점 }}</p>
                <ml-textarea v-else v-model="item.문제점" :readonly="board.readonly" rows="6" :placeholder="reportProblemPlaceholder" class="w-full mb-0" />
              </td>
              <td>
                <p v-if="isPrint" class="print-textarea no-block">{{ item.근로자의견 }}</p>
                <ml-textarea v-else v-model="item.근로자의견" :readonly="board.readonly" rows="6" placeholder="인력 충원 및 인력 수거를 최소한으로 하는 방안 마련" class="w-full mb-0" />
              </td>
              <td>
                <p v-if="isPrint" class="print-textarea no-block">{{ item.개선방안 }}</p>
                <ml-textarea v-else v-model="item.개선방안" :readonly="board.readonly" rows="6" :placeholder="reportPlanPlaceholder" class="w-full mb-0" />
              </td>
              <td>
                <p class="cell-label">추진 일정</p>
                <p v-if="isPrint" class="print-input">{{ item.추진일정 }}</p>
                <ml-input v-else v-model="item.추진일정" :readonly="board.readonly" :placeholder="schedulePlaceholder" class="w-full" />
                <p class="cell-label mt-3">개선비용(만원)</p>
                <p v-if="isPrint" class="print-input">{{ item.개선비용 }}</p>
                <comma-input v-else v-model="item.개선비용" :readonly="board.readonly" placeholder="2,000" class="w-full" />
              </td>
              <td>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <p class="cell-label flex items-center">
                      종합
                      <vx-tooltip v-if="!isPrint" title="종합" :text="PRIORITY_NOTES.종합" style="margin-left: 4px; display: inline-flex; align-items: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" class="info-icon" />
                      </vx-tooltip>
                    </p>
                    <p v-if="isPrint" class="print-input">{{ item.종합 }}</p>
                    <ml-input v-else v-model="item.종합" :readonly="board.readonly" placeholder="12+" class="w-full" />
                  </div>
                  <div>
                    <p class="cell-label flex items-center">
                      총 점수
                      <vx-tooltip v-if="!isPrint" title="총점수" :text="PRIORITY_NOTES.총점수" style="margin-left: 4px; display: inline-flex; align-items: center" position="top" delay=".1s">
                        <feather-icon icon="AlertCircleIcon" class="info-icon" />
                      </vx-tooltip>
                    </p>
                    <p v-if="isPrint" class="print-input">{{ item.총점수 }}</p>
                    <ml-input v-else :value="item.총점수" :readonly="board.readonly" placeholder="12" class="w-full" @input="(text) => onScoreInput(item, text)" />
                  </div>
                </div>
                <p class="cell-label mt-3">증상 호소 여부</p>
                <p v-if="isPrint" class="print-textarea no-block">{{ item.증상호소여부 }}</p>
                <ml-textarea v-else v-model="item.증상호소여부" :readonly="board.readonly" rows="3" placeholder="어깨, 팔, 손, 허리" class="w-full mb-0" />
              </td>
            </tr>
            <tr v-if="form.개선계획목록.length === 0">
              <td :colspan="editable ? 8 : 7" class="text-center empty">{{ isPrint ? "-" : "항목을 추가해주세요" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="note mt-3">※ 총점수 : {{ PRIORITY_NOTES.총점수 }}</p>
      <p class="note">※ {{ PRIORITY_NOTES.종합 }}</p>
    </div>

    <!-- 작업환경 개선계획 및 결과보고서 -->
    <div class="section">
      <div class="flex items-center justify-between mb-4">
        <h5 class="section-title mb-0 flex items-center flex-wrap gap-2">
          <span><i class="dot"></i>작업환경 개선계획 및 결과보고서</span>
          <span class="note font-normal">※ 유해요인조사 실시 후 개선계획 및 결과보고서를 작성하여 자체 보관하여야 합니다.</span>
        </h5>
        <div v-if="editable" class="flex-shrink-0">
          <vs-button color="secondary" @click="addReport">항목추가</vs-button>
          <vs-button class="ml-4" color="secondary" type="border" :disabled="reportCheckIds.length === 0" @click="removeReports">선택삭제</vs-button>
        </div>
      </div>
      <div class="table-scroll">
        <table class="doc-table report-table">
          <colgroup>
            <col v-if="editable" width="36" />
            <col width="11%" />
            <col width="11%" />
            <col width="11%" />
            <col width="21%" />
            <col width="21%" />
            <col width="21%" />
          </colgroup>
          <thead>
            <tr>
              <th v-if="editable" class="p-0"><vs-checkbox :value="form.결과보고목록.length > 0 && reportCheckIds.length === form.결과보고목록.length" class="check" @click.native="toggleAllReports" /></th>
              <th>부서명</th>
              <th>작업명</th>
              <th>단위 작업명</th>
              <th>문제점 (유해요인 원인)</th>
              <th>개선계획</th>
              <th>개선결과</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in form.결과보고목록" :key="`report_${item.id}`">
              <td v-if="editable" class="p-0 check-cell"><vs-checkbox v-model="reportCheckIds" :vs-value="item.id" class="check" /></td>
              <td class="text-center auto-cell">
                {{ form.stdInfo.부서명_과 || "-" }}
              </td>
              <td class="text-center auto-cell">
                {{ form.조사정보.작업명 || "-" }}
              </td>
              <td>
                <p v-if="isPrint" class="print-input">{{ item.단위작업명 }}</p>
                <ml-input v-else v-model="item.단위작업명" :readonly="board.readonly" placeholder="가로 청소 작업" class="w-full" />
              </td>
              <td>
                <div v-if="isPrint" class="print-photo small">
                  <img v-if="item.문제점사진 && item.문제점사진.path" :src="item.문제점사진.path" />
                </div>
                <div v-else class="photo-box small">
                  <file-uploader-transparent v-model="item.문제점사진" :readonly="board.readonly" style="background-size: contain; background-repeat: no-repeat" @progress="(state) => onPhotoProgress(item, state, '문제점사진업로드중')" />
                  <simple-spinner v-if="item.문제점사진업로드중" class="photo-spinner" />
                </div>
                <p v-if="isPrint" class="print-textarea no-block mt-2">{{ item.문제점 }}</p>
                <ml-textarea v-else v-model="item.문제점" :readonly="board.readonly" rows="6" :placeholder="reportProblemPlaceholder" class="w-full mb-0 mt-2" />
              </td>
              <td>
                <p v-if="isPrint" class="print-textarea no-block">{{ item.개선계획 }}</p>
                <ml-textarea v-else v-model="item.개선계획" :readonly="board.readonly" rows="8" :placeholder="reportPlanPlaceholder" class="w-full mb-0" />
              </td>
              <td>
                <div v-if="isPrint" class="print-photo small">
                  <img v-if="item.개선결과사진 && item.개선결과사진.path" :src="item.개선결과사진.path" />
                </div>
                <div v-else class="photo-box small">
                  <file-uploader-transparent v-model="item.개선결과사진" :readonly="board.readonly" style="background-size: contain; background-repeat: no-repeat" @progress="(state) => onPhotoProgress(item, state, '개선결과사진업로드중')" />
                  <simple-spinner v-if="item.개선결과사진업로드중" class="photo-spinner" />
                </div>
                <p v-if="isPrint" class="print-textarea no-block mt-2">{{ item.개선결과 }}</p>
                <ml-textarea v-else v-model="item.개선결과" :readonly="board.readonly" rows="6" :placeholder="reportResultPlaceholder" class="w-full mb-0 mt-2" />
                <p class="cell-label mt-3">개선완료일</p>
                <p v-if="isPrint" class="print-input">{{ item.개선완료일 }}</p>
                <date-selector v-else v-model="item.개선완료일" :readonly="board.readonly" placeholder="날짜선택" class="w-full" />
              </td>
            </tr>
            <tr v-if="form.결과보고목록.length === 0">
              <td :colspan="editable ? 7 : 6" class="text-center empty">{{ isPrint ? "-" : "항목을 추가해주세요" }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="grid grid-cols-12 gap-x-6 gap-y-4 mt-4">
        <div class="col-span-12 xl:col-span-4 flex items-center">
          <div class="label-text">작성일자</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.결과보고정보.작성일자 }}</p>
          <date-selector v-else v-model="form.결과보고정보.작성일자" :readonly="board.readonly" placeholder="날짜선택" class="w-full flex-1" />
        </div>
        <div class="col-span-12 xl:col-span-4 flex items-center">
          <div class="label-text">작성부서</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.결과보고정보.작성부서 }}</p>
          <ml-input v-else v-model="form.결과보고정보.작성부서" :readonly="board.readonly" placeholder="작성부서" class="w-full flex-1" />
        </div>
        <div class="col-span-12 xl:col-span-4 flex items-center">
          <div class="label-text">담당자</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.결과보고정보.담당자 }}</p>
          <ml-input v-else v-model="form.결과보고정보.담당자" :readonly="board.readonly" placeholder="담당자" class="w-full flex-1" />
        </div>
        <div class="col-span-12 flex items-start">
          <div class="label-text mt-2">특이사항</div>
          <p v-if="isPrint" class="print-textarea flex-1">{{ form.특이사항 }}</p>
          <ml-textarea v-else v-model="form.특이사항" :readonly="board.readonly" rows="5" placeholder="증상조사표의 고위험군 종사자는 5월 산업보건의 활동시 건강상담 진행예정" class="w-full flex-1 mb-0" />
        </div>
      </div>
    </div>

    <!-- 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-6" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly" />

    <!-- 근골격계부담작업 구분 팝업 -->
    <vs-popup class="burden-popup" title="근골격계부담작업 구분" :active.sync="showBurdenPopup">
      <div class="p-2">
        <img src="@/assets/images/부담작업/근골격계부담작업의_범위.jpg" alt="근골격계부담작업의 범위" class="w-full" />
      </div>
    </vs-popup>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule";
import DateSelector from "@/components/selector/DateSelector";
import FileUploaderTransparent from "@/components/FileUploaderTransparent2";
import SimpleSpinner from "@/components/spinner/SimpleSpinner.vue";
import SituationRadioRow from "./SituationRadioRow.vue";
import * as UiUtil from "@/util/UiUtil";
import iconPlus from "@/assets/images/icon/icon-plus.svg";
import iconMinus from "@/assets/images/icon/icon-minus.svg";

// 조사구분 체크 항목 (작성·인쇄 공용)
const CheckItem = {
  name: "CheckItem",
  props: { value: Boolean, label: String, isPrint: Boolean, readonly: Boolean },
  render(h) {
    if (this.isPrint) {
      return h("span", { class: "print-check" }, [
        h("i", { class: "print-check-icon" }, this.value ? [h("feather-icon", { props: { icon: "CheckIcon", svgClasses: "w-4 h-4" } })] : []),
        h("span", this.label),
      ]);
    }
    return h(
      "vs-checkbox",
      {
        props: { value: this.value, disabled: this.readonly },
        on: { input: (checked) => this.$emit("input", !!checked) },
      },
      this.label
    );
  },
};

// "+ 항목추가 | - 마지막항목삭제" 텍스트 버튼 (452 검사결과 표와 같은 flat 버튼 + 구분선 패턴)
const RowControls = {
  name: "RowControls",
  props: { addLabel: { type: String, default: "항목추가" }, removeLabel: { type: String, default: "마지막항목삭제" } },
  render(h) {
    return h("div", { class: "row-controls flex items-center" }, [
      h("vs-button", { props: { color: "secondary", type: "flat" }, on: { click: () => this.$emit("add") } }, [h("img", { attrs: { src: iconPlus }, class: "inline-block align-middle" }), ` ${this.addLabel}`]),
      h("hr", { style: { height: "20px", borderWidth: "1px", borderColor: "#6670cb", borderLeft: "0" } }),
      h("vs-button", { props: { color: "secondary", type: "flat" }, on: { click: () => this.$emit("remove-last") } }, [h("img", { attrs: { src: iconMinus }, class: "inline-block align-middle" }), ` ${this.removeLabel}`]),
    ]);
  },
};

// 개선계획서 개선우선순위 설명 — 라벨 옆 툴팁과 표 아래 안내문이 같은 문구를 쓴다
const PRIORITY_NOTES = {
  총점수: "유해요인 기본조사표의 총점수(A×B)를 의미",
  종합: "증상이 있는 경우 종합점수에 + 표시, 동일한 점수 중 + 표시가 있는 공정을 우선순위로 선정",
};

const SITUATION_ITEMS = [
  { value: "변화 없음", textInput: false },
  { value: "줄음", textInput: true },
  { value: "늘어남", textInput: true },
  { value: "기타", textInput: true },
];
const SITUATION_ITEMS_EQUIPMENT = [
  { value: "변화 없음", textInput: false },
  { value: "변화 있음", textInput: true },
];

export default {
  components: {
    FieldSelectModule,
    FileUploadModule,
    DateSelector,
    FileUploaderTransparent,
    SimpleSpinner,
    SituationRadioRow,
    CheckItem,
    RowControls,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      form: {
        stdInfo: {},
        조사구분: { 정기조사: false, 근골격계질환자발생시: false, 새로운작업설비도입시: false, 작업환경변경: false },
        // 목록 표시·검색용 요약 ("정기조사, 수시조사")
        조사구분요약: "",
        조사정보: { 조사일시: "", 조사자: "", 작업공정명: "", 작업명: "" },
        상황조사: {
          작업설비: { value: "", text: "" },
          작업량: { value: "", text: "" },
          작업속도: { value: "", text: "" },
          업무변화: { value: "", text: "" },
        },
        작업내용목록: [], // 1단계 [{ id, 내용 }]
        단위작업목록: [], // 2단계 [{ id, 단위작업명, 부담작업, 작업부하, 작업빈도, 총점수 }]
        유해요인평가: { 직종명: "", 근로자수: "", 유해요인설명: "" },
        평가사진: [
          { id: 1, image: null },
          { id: 2, image: null },
        ],
        원인분석목록: [], // [{ id, 원본단위작업id(2단계 행 id, 없으면 독립), 동기화값, 단위작업명, 부담작업, 유해요인목록: [{ id, 유해요인, 원인 }] }]
        개선계획목록: [], // [{ id, 단위작업명, 문제점, 근로자의견, 개선방안, 추진일정, 개선비용, 종합, 총점수, 증상호소여부 }]
        결과보고목록: [], // [{ id, 단위작업명, 문제점사진, 문제점, 개선계획, 개선결과사진, 개선결과, 개선완료일 }]
        결과보고정보: { 작성일자: "", 작성부서: "", 담당자: "" },
        특이사항: "",
        fileList: [],
        numberId: 0,
      },
      planCheckIds: [],
      reportCheckIds: [],
      showBurdenPopup: false,
      scoreOptions: ["1", "2", "3", "4", "5"],
      scoreGuide: [
        { load: "매우 쉬움", score: 1, frequency: "3개월 마다(년2~3회)" },
        { load: "쉬움", score: 2, frequency: "가끔(하루 또는 주2~3일)" },
        { load: "약간 힘듦", score: 3, frequency: "자주(1일 4시간)" },
        { load: "힘듦", score: 4, frequency: "계속(1일 4시간 이상)" },
        { load: "매우 힘듦", score: 5, frequency: "초과근무 시간(1일 8시간 이상)" },
      ],
      situationItems: SITUATION_ITEMS,
      PRIORITY_NOTES,
      situationItemsEquipment: SITUATION_ITEMS_EQUIPMENT,
    };
  },
  computed: {
    editable() {
      return !this.isPrint && !this.board.readonly;
    },
    // 개선계획서·결과보고서 공용 문제점 안내 문구
    reportProblemPlaceholder() {
      return [
        "* 거리에 버려진 폐기물을 빗자루를 이용한 쓸기나 삽으로 포대에 담는 작업으로 어깨, 팔/팔꿈치, 손/손목의 반복성이 발생함.",
        "",
        "* 폐기물을 빗자루로 쓰레받이에 쓸거나 삽으로 포대에 담는 작업으로 인해 허리를 구부리거나 비트는 행위 발생함.",
      ].join("\n");
    },
    // 개선계획서 개선방안·결과보고서 개선계획 공용 안내 문구
    reportPlanPlaceholder() {
      return [
        "1. 작업 전, 중, 후 스트레칭 체조 실시",
        "2. 근로자 충분한 휴식",
        "3. 필요시 허리, 손목 보호대 지급",
        "4. 낙엽을 흡입하는 흡입기 지급",
        "5. 거리 청소용 진공청소기 지급",
        "6. 안마기 비치로 근육 긴장 완화",
        "7. 요양복귀자 및 고위험군 산업보건의 활동시 면담 진행",
      ].join("\n");
    },
    // 결과보고서 개선결과 안내 문구 ("개선내용:" 다음 줄에 "- ")
    reportResultPlaceholder() {
      return ["개선내용:", "- "].join("\n");
    },
    // 추진 일정 안내 문구: 올해 두 자리 연도 (예: "26.1~3월")
    schedulePlaceholder() {
      return `${String(new Date().getFullYear()).slice(-2)}.1~3월`;
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (!value) return;
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        if (this.board && this.board.writer) value.title = this.board.writer.department;

        // 조사구분 요약 (목록 표시·검색용)
        const summary = [];
        if (value.조사구분.정기조사) summary.push("정기조사");
        if (value.조사구분.근골격계질환자발생시 || value.조사구분.새로운작업설비도입시 || value.조사구분.작업환경변경) summary.push("수시조사");
        const summaryText = summary.join(", ");
        if (value.조사구분요약 !== summaryText) value.조사구분요약 = summaryText;

        // 2단계 총점수 = 작업부하(A) × 작업빈도(B)
        for (const item of value.단위작업목록) {
          const load = Number(item.작업부하);
          const frequency = Number(item.작업빈도);
          const total = load && frequency ? String(load * frequency) : "";
          if (item.총점수 !== total) item.총점수 = total;
        }

        // 2단계 단위작업명·부담작업(호) → 연결된 3단계 원인분석 표
        this.syncCauseUnits(value);

        // 개선계획서 총점수: 2단계 표에서 같은 단위작업명의 총점수를 자동 입력 (일치하는 항목이 없으면 직접 입력값 유지)
        for (const plan of value.개선계획목록) {
          const name = (plan.단위작업명 || "").trim();
          if (!name) continue;
          const matched = value.단위작업목록.find((unit) => (unit.단위작업명 || "").trim() === name && unit.총점수);
          if (matched && plan.총점수 !== matched.총점수) plan.총점수 = matched.총점수;
        }

        this.$emit("input", value);
      },
    },
  },
  methods: {
    nextId() {
      return ++this.form.numberId;
    },
    removeLast(list) {
      if (list && list.length > 0) list.pop();
    },

    // 1단계
    addTaskContent() {
      this.form.작업내용목록.push({ id: this.nextId(), 내용: "" });
    },
    // 2단계: 단위작업을 추가하면 3단계 원인분석 표도 함께 만들고(원본단위작업id 로 연결),
    //        2단계의 단위작업명·부담작업(호) 입력이 연결된 3단계 표에 따라 들어간다 (form watcher 의 syncCauseUnits)
    addUnitTask() {
      const unit = { id: this.nextId(), 단위작업명: "", 부담작업: "", 작업부하: "", 작업빈도: "", 총점수: "" };
      this.form.단위작업목록.push(unit);
      this.form.원인분석목록.push(this.createCauseUnit(unit.id));
    },
    removeLastUnitTask() {
      const removed = this.form.단위작업목록.pop();
      if (removed) this.form.원인분석목록 = this.form.원인분석목록.filter((cause) => cause.원본단위작업id !== removed.id);
    },
    // 3단계 원인분석 (원본단위작업id 가 없으면 2단계와 연결되지 않은 독립 표)
    createCauseUnit(sourceId) {
      return { id: this.nextId(), 원본단위작업id: sourceId || null, 동기화값: { 단위작업명: "", 부담작업: "" }, 단위작업명: "", 부담작업: "", 유해요인목록: [] };
    },
    addCauseUnit() {
      this.form.원인분석목록.push(this.createCauseUnit(null));
    },
    // 2단계 → 3단계 동기화: 2단계 값이 바뀐 경우에만 덮어쓴다 (3단계에서 직접 고친 값은 2단계가 다시 바뀌기 전까지 유지)
    syncCauseUnits(value) {
      for (const cause of value.원인분석목록) {
        if (!cause.원본단위작업id) continue;
        const source = value.단위작업목록.find((unit) => unit.id === cause.원본단위작업id);
        if (!source) continue;
        if (!cause.동기화값) this.$set(cause, "동기화값", { 단위작업명: "", 부담작업: "" });
        for (const key of ["단위작업명", "부담작업"]) {
          const sourceValue = source[key] || "";
          if (sourceValue !== cause.동기화값[key]) {
            cause[key] = sourceValue;
            cause.동기화값[key] = sourceValue;
          }
        }
      }
    },
    addCauseFactor(unit) {
      unit.유해요인목록.push({ id: this.nextId(), 유해요인: "", 원인: "" });
    },

    // 개선계획서
    addPlan() {
      this.form.개선계획목록.push({ id: this.nextId(), 단위작업명: "", 문제점: "", 근로자의견: "", 개선방안: "", 추진일정: "", 개선비용: "", 종합: "", 총점수: "", 증상호소여부: "" });
    },
    removePlans() {
      this.form.개선계획목록 = this.form.개선계획목록.filter((item) => !this.planCheckIds.includes(item.id));
      this.planCheckIds = [];
    },
    // 개선우선순위 총점수: 숫자만 입력 (숫자 외 문자는 지운다)
    onScoreInput(item, text) {
      const raw = text === null || text === undefined ? "" : String(text);
      const digits = raw.replace(/\D/g, "");
      if (digits === raw) {
        item.총점수 = digits;
        return;
      }
      // 입력 컴포넌트가 걸러낸 값으로 다시 그려지도록 값 변경을 두 번에 나눠 적용한다
      item.총점수 = raw;
      this.$nextTick(() => {
        item.총점수 = digits;
      });
    },
    toggleAllPlans() {
      if (this.planCheckIds.length === this.form.개선계획목록.length) this.planCheckIds = [];
      else this.planCheckIds = this.form.개선계획목록.map((item) => item.id);
    },

    // 결과보고서
    addReport() {
      this.form.결과보고목록.push({ id: this.nextId(), 단위작업명: "", 문제점사진: null, 문제점: "", 개선계획: "", 개선결과사진: null, 개선결과: "", 개선완료일: "" });
    },
    removeReports() {
      this.form.결과보고목록 = this.form.결과보고목록.filter((item) => !this.reportCheckIds.includes(item.id));
      this.reportCheckIds = [];
    },
    toggleAllReports() {
      if (this.reportCheckIds.length === this.form.결과보고목록.length) this.reportCheckIds = [];
      else this.reportCheckIds = this.form.결과보고목록.map((item) => item.id);
    },

    // 이미지 업로드 진행 표시
    onPhotoProgress(target, state, key = "uploading") {
      this.$set(target, key, state === "start");
      if (state === "success") this.$toast.open({ type: "default", message: "이미지 업로드가 완료되었습니다.", position: "bottom", duration: 2000 });
      if (state === "fail") this.$toast.open({ type: "error", message: "이미지 업로드가 실패했습니다.", position: "bottom", duration: 2000 });
    },

    // 저장 전 필수값 검증 (통과 true / 실패 false)
    validate() {
      if (!this.form.stdInfo || !this.form.stdInfo.field_name) {
        const fieldTitle = (this.$refs.fieldSelectModule && this.$refs.fieldSelectModule.fieldTitle) || "사업장명";
        UiUtil.alertDialog(`${fieldTitle}을(를) 반드시 입력해야 합니다`);
        return false;
      }
      return true;
    },
  },
};
</script>

<style scoped lang="scss">
.section {
  margin-top: 28px;
}
.section-title {
  margin-bottom: 16px;
}
.step-title {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 12px;
}
.label-text {
  width: 114px;
  min-width: 114px;
  font-weight: 500;
  font-size: 14px;
  padding-right: 12px;
}
.hint {
  font-size: 12px;
  color: #999;
  font-weight: 400;
  margin-left: 6px;
}
.note {
  font-size: 13px;
  color: #555;
  line-height: 20px;
}
.empty-message {
  padding: 20px;
  text-align: center;
  color: #999;
  border: 1px dashed #cdcdcd;
}
.info-icon {
  width: 16px;
  color: #7e72f2;
  cursor: pointer;
}

// 조사구분
.survey-type-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-bottom: 10px;
}
.survey-type-label {
  width: 80px;
  min-width: 80px;
  font-size: 14px;
}
::v-deep .print-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 12px;
  .print-check-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: 2px solid #cdcdcd;
  }
}

// 표
.table-scroll {
  width: 100%;
  overflow-x: auto;
}
table.doc-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 8px;
    font-size: 14px;
    vertical-align: middle;
    word-break: break-all;
  }
  th {
    background: #f9f9f9;
    font-weight: 500;
    text-align: center;
    line-height: 18px;
    &.text-left {
      text-align: left;
    }
  }
  td.text-right {
    text-align: right;
  }
  td.empty {
    color: #999;
    padding: 20px;
  }
  td.auto-cell {
    background: #fcfcfc;
  }
  td.photo-cell {
    padding: 12px;
  }
}
table.score-guide {
  td {
    background: #fff;
  }
}
table.plan-table,
table.report-table {
  min-width: 960px;
  td {
    vertical-align: top;
  }
  // 행 선택 체크박스는 셀 가운데(상하·좌우)에 둔다
  td.check-cell {
    vertical-align: middle;
  }
}
.cell-label {
  font-size: 12px;
  color: #555;
  margin-bottom: 4px;
  white-space: nowrap;
}
.check::v-deep {
  justify-content: center !important;
  span {
    margin: 0;
  }
}

// "+ 추가 | - 마지막항목삭제" (텍스트형 flat 버튼)
// "+ 추가 | - 마지막항목삭제" 버튼의 배경 제거는 아래 비-scoped 스타일 블록에서 처리한다
// (scoped + ::v-deep 안의 background 는 postcss-rtl 이 [dir][data-v] 선택자로 바꿔 적용되지 않는다)

// 사진
.photo-box {
  position: relative;
  width: 100%;
  height: 260px;
  border: 1px dashed #cdcdcd;
  &.small {
    height: 160px;
  }
  ::v-deep label.only-image {
    display: flex;
    width: 100%;
    height: 100%;
  }
}
.photo-spinner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #cdcdcdaa;
}
.print-photo {
  width: 100%;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid #eee;
  &.small {
    height: 160px;
  }
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.burden-popup::v-deep .vs-popup {
  width: 760px;
  max-width: 95vw;
}
</style>

<style lang="scss">
// 3단계 유해요인 설명: 입력 글자·안내문을 가운데 정렬 (text-align 은 scoped+::v-deep 에서 적용되지 않아 여기서 처리)
.write-1237 .centered-input input,
.write-1237 .centered-input .vs-input--placeholder {
  text-align: center;
}

// 원인분석 표 안의 "유해요인추가 | 마지막항목삭제" 줄: 셀·버튼 여백을 절반으로 줄이고 오른쪽 정렬
// (vuesax 의 .vs-button:not(...) 규칙과 scoped td 규칙보다 우선하도록 선택자를 구체화하고 !important 를 준다)
.write-1237 table.doc-table td.cause-controls {
  padding: 4px !important;
  .row-controls {
    justify-content: flex-end;
  }
  // 좌우 여백은 바로 위 "단위작업추가" 버튼(28px)의 절반
  .row-controls .vs-button {
    padding: 3px 14px !important;
  }
}

// 원인분석: 입력 중인 단위작업 표 강조 (테두리 두껍게 + 헤더 색 변경)
.write-1237 .cause-table-wrap {
  padding: 2px;
  &:focus-within table.cause-table {
    box-shadow: 0 0 0 2px #7e72f2;
    th {
      background-color: #efedfd;
      color: #5b50d6;
    }
  }
}

// 1237 전용: 텍스트형 추가/삭제 버튼에서 클릭·호버 시 칠해지는 파란 배경(물결 효과)을 없앤다
.write-1237 .row-controls .vs-button {
  padding: 6px 10px;
  font-size: 13px;
  white-space: nowrap;
  background: transparent !important;
  box-shadow: none !important;

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
  }

  .vs-button--background,
  .vs-button-backgroundx {
    display: none !important;
  }

  // 클릭(isActive) 시 vuesax 가 글자·아이콘을 흰색으로 바꾸므로 버튼 색을 유지한다
  &.isActive .vs-button--text,
  &.isActive .vs-button--icon,
  .vs-button--text {
    color: rgba(var(--vs-secondary), 1) !important;
  }
}
</style>
